import { DOMEvent, camelToKebabCase, effect, isFunction, isString, isUndefined, listenEvent, peek } from "../chunks/vidstack-D9mvYVIz.js";
import { ListSymbol, RAFLoop } from "../chunks/vidstack-ijoHbXQB.js";
import { IS_CHROME, isHLSSupported } from "../chunks/vidstack-Dtnbh_MF.js";
import { loadScript, preconnect } from "../chunks/vidstack-mvRkf9tF.js";
import { TextTrack, TextTrackSymbol } from "../chunks/vidstack-DYPyTdYw.js";
import { QualitySymbol } from "../chunks/vidstack-_E7LrUjK.js";
import { VideoProvider } from "../chunks/vidstack-C6nBVWwn.js";
import { coerceToError } from "../chunks/vidstack-CDOoBnJL.js";
//#region src/providers/hls/hls.ts
const toDOMEventType = (type) => camelToKebabCase(type);
var HLSController = class {
	#video;
	#ctx;
	#instance = null;
	#stopLiveSync = null;
	config = {};
	#callbacks = /* @__PURE__ */ new Set();
	get instance() {
		return this.#instance;
	}
	constructor(video, ctx) {
		this.#video = video;
		this.#ctx = ctx;
	}
	setup(ctor) {
		const { streamType } = this.#ctx.$state;
		const isLive = peek(streamType).includes("live"), isLiveLowLatency = peek(streamType).includes("ll-");
		const instance = this.#instance = new ctor({
			lowLatencyMode: isLiveLowLatency,
			backBufferLength: isLiveLowLatency ? 4 : isLive ? 8 : void 0,
			renderTextTracksNatively: false,
			...this.config
		});
		const guard = (callback) => (eventType, data) => {
			if (this.#instance !== instance) return;
			callback(eventType, data);
		};
		const dispatcher = guard(this.#dispatchHLSEvent.bind(this));
		for (const event of Object.values(ctor.Events)) instance.on(event, dispatcher);
		instance.on(ctor.Events.ERROR, guard(this.#onError.bind(this)));
		for (const callback of this.#callbacks) callback(instance);
		this.#ctx.player.dispatch("hls-instance", { detail: instance });
		instance.attachMedia(this.#video);
		instance.on(ctor.Events.AUDIO_TRACK_SWITCHED, guard(this.#onAudioSwitch.bind(this)));
		instance.on(ctor.Events.LEVEL_SWITCHED, guard(this.#onLevelSwitched.bind(this)));
		instance.on(ctor.Events.LEVEL_LOADED, guard(this.#onLevelLoaded.bind(this, instance)));
		instance.on(ctor.Events.LEVEL_UPDATED, guard(this.#onLevelUpdated.bind(this)));
		instance.on(ctor.Events.NON_NATIVE_TEXT_TRACKS_FOUND, guard(this.#onTracksFound.bind(this, instance)));
		instance.on(ctor.Events.CUES_PARSED, guard(this.#onCuesParsed.bind(this, instance)));
		this.#ctx.qualities[QualitySymbol.enableAuto] = this.#enableAutoQuality.bind(this);
		listenEvent(this.#ctx.qualities, "change", this.#onUserQualityChange.bind(this));
		listenEvent(this.#ctx.audioTracks, "change", this.#onUserAudioChange.bind(this));
		this.#stopLiveSync = effect(this.#liveSync.bind(this));
	}
	#createDOMEvent(type, data) {
		return new DOMEvent(toDOMEventType(type), { detail: data });
	}
	#liveSync() {
		if (!this.#ctx.$state.live()) return;
		const raf = new RAFLoop(this.#liveSyncPosition.bind(this));
		raf.start();
		return raf.stop.bind(raf);
	}
	#liveSyncPosition() {
		this.#ctx.$state.liveSyncPosition.set(this.#instance?.liveSyncPosition ?? Infinity);
	}
	#dispatchHLSEvent(type, data) {
		this.#ctx.player?.dispatch(this.#createDOMEvent(type, data));
	}
	#onTracksFound(instance, eventType, data) {
		const event = this.#createDOMEvent(eventType, data);
		let currentTrack = -1;
		for (let i = 0; i < data.tracks.length; i++) {
			const nonNativeTrack = data.tracks[i], init = nonNativeTrack.subtitleTrack ?? nonNativeTrack.closedCaptions, track = new TextTrack({
				id: `hls-${nonNativeTrack.kind}-${i}`,
				src: init?.url,
				label: nonNativeTrack.label,
				language: init?.lang,
				kind: nonNativeTrack.kind,
				default: nonNativeTrack.default
			});
			track[TextTrackSymbol.readyState] = 2;
			track[TextTrackSymbol.onModeChange] = () => {
				if (this.#instance !== instance) return;
				if (track.mode === "showing") {
					instance.subtitleTrack = i;
					currentTrack = i;
				} else if (currentTrack === i) {
					instance.subtitleTrack = -1;
					currentTrack = -1;
				}
			};
			this.#ctx.textTracks.add(track, event);
		}
	}
	#onCuesParsed(instance, eventType, data) {
		const index = instance.subtitleTrack, track = this.#ctx.textTracks.getById(`hls-${data.type}-${index}`);
		if (!track) return;
		const event = this.#createDOMEvent(eventType, data);
		for (const cue of data.cues) {
			cue.positionAlign = "auto";
			track.addCue(cue, event);
		}
	}
	#onAudioSwitch(eventType, data) {
		const track = this.#ctx.audioTracks[data.id];
		if (track) {
			const trigger = this.#createDOMEvent(eventType, data);
			this.#ctx.audioTracks[ListSymbol.select](track, true, trigger);
		}
	}
	#onLevelSwitched(eventType, data) {
		const quality = this.#ctx.qualities[data.level];
		if (quality) {
			const trigger = this.#createDOMEvent(eventType, data);
			this.#ctx.qualities[ListSymbol.select](quality, true, trigger);
		}
	}
	#onLevelUpdated(eventType, data) {
		if (data.details.totalduration > 0) this.#ctx.$state.inferredLiveDVRWindow.set(data.details.totalduration);
	}
	#onLevelLoaded(instance, eventType, data) {
		if (this.#ctx.$state.canPlay()) return;
		const media = instance.media;
		if (!media) return;
		const { type, live, totalduration: duration, targetduration } = data.details, trigger = this.#createDOMEvent(eventType, data);
		this.#ctx.notify("stream-type-change", live ? type === "EVENT" && Number.isFinite(duration) && targetduration >= 10 ? "live:dvr" : "live" : "on-demand", trigger);
		this.#ctx.notify("duration-change", duration, trigger);
		if (instance.currentLevel === -1) this.#ctx.qualities[QualitySymbol.setAuto](true, trigger);
		for (const remoteTrack of instance.audioTracks) {
			const localTrack = {
				id: remoteTrack.id.toString(),
				label: remoteTrack.name,
				language: remoteTrack.lang || "",
				kind: "main"
			};
			this.#ctx.audioTracks[ListSymbol.add](localTrack, trigger);
		}
		for (const level of instance.levels) {
			const videoQuality = {
				id: level.id?.toString() ?? level.height + "p",
				width: level.width,
				height: level.height,
				codec: level.codecSet,
				bitrate: level.bitrate
			};
			this.#ctx.qualities[ListSymbol.add](videoQuality, trigger);
		}
		media.dispatchEvent(new DOMEvent("canplay", { trigger }));
	}
	#onError(eventType, data) {
		if (data.fatal) switch (data.type) {
			case "mediaError":
				this.#instance?.recoverMediaError();
				break;
			default:
				this.#onFatalError(data.error);
				break;
		}
	}
	#onFatalError(error) {
		this.#ctx.notify("error", {
			message: error.message,
			code: 1,
			error
		});
	}
	#enableAutoQuality() {
		if (this.#instance) this.#instance.currentLevel = -1;
	}
	#onUserQualityChange() {
		const { qualities } = this.#ctx;
		if (!this.#instance || qualities.auto) return;
		this.#instance[qualities.switch + "Level"] = qualities.selectedIndex;
		/**
		* Chrome has some strange issue with detecting keyframes inserted before the current
		* playhead position. This can cause playback to freeze until a new keyframe. It seems
		* setting the current time forces chrome to seek back to the last keyframe and adjust
		* playback. Weird fix, but it works!
		*/
		if (IS_CHROME) this.#video.currentTime = this.#video.currentTime;
	}
	#onUserAudioChange() {
		const { audioTracks } = this.#ctx;
		if (this.#instance && this.#instance.audioTrack !== audioTracks.selectedIndex) this.#instance.audioTrack = audioTracks.selectedIndex;
	}
	onInstance(callback) {
		this.#callbacks.add(callback);
		return () => this.#callbacks.delete(callback);
	}
	loadSource(src) {
		if (!isString(src.src)) return;
		this.#instance?.loadSource(src.src);
	}
	destroy() {
		this.#instance?.destroy();
		this.#instance = null;
		this.#stopLiveSync?.();
		this.#stopLiveSync = null;
	}
};
//#endregion
//#region src/providers/hls/lib-loader.ts
var HLSLibLoader = class {
	#lib;
	#ctx;
	#callback;
	constructor(lib, ctx, callback) {
		this.#lib = lib;
		this.#ctx = ctx;
		this.#callback = callback;
		this.#startLoading();
	}
	async #startLoading() {
		const callbacks = {
			onLoadStart: this.#onLoadStart.bind(this),
			onLoaded: this.#onLoaded.bind(this),
			onLoadError: this.#onLoadError.bind(this)
		};
		let ctor = await loadHLSScript(this.#lib, callbacks);
		if (isUndefined(ctor) && !isString(this.#lib)) ctor = await importHLS(this.#lib, callbacks);
		if (!ctor) return null;
		if (!ctor.isSupported()) {
			const message = "[vidstack] `hls.js` is not supported in this environment";
			this.#ctx.player.dispatch(new DOMEvent("hls-unsupported"));
			this.#ctx.notify("error", {
				message,
				code: 4
			});
			return null;
		}
		return ctor;
	}
	#onLoadStart() {
		this.#ctx.player.dispatch(new DOMEvent("hls-lib-load-start"));
	}
	#onLoaded(ctor) {
		this.#ctx.player.dispatch(new DOMEvent("hls-lib-loaded", { detail: ctor }));
		this.#callback(ctor);
	}
	#onLoadError(e) {
		const error = coerceToError(e);
		this.#ctx.player.dispatch(new DOMEvent("hls-lib-load-error", { detail: error }));
		this.#ctx.notify("error", {
			message: error.message,
			code: 4,
			error
		});
	}
};
async function importHLS(loader, callbacks = {}) {
	if (isUndefined(loader)) return void 0;
	callbacks.onLoadStart?.();
	if (loader.prototype && loader.prototype !== Function) {
		callbacks.onLoaded?.(loader);
		return loader;
	}
	try {
		const ctor = (await loader())?.default;
		if (ctor && !!ctor.isSupported) callbacks.onLoaded?.(ctor);
		else throw Error("");
		return ctor;
	} catch (err) {
		callbacks.onLoadError?.(err);
	}
}
/**
* Loads `hls.js` from the remote source given via `library` into the window namespace. This
* is because `hls.js` in {currentYear} still doesn't provide a ESM export. This method will
* return `undefined` if it fails to load the script. Listen to `hls-lib-load-error` to be
* notified of any failures.
*/
async function loadHLSScript(src, callbacks = {}) {
	if (!isString(src)) return void 0;
	callbacks.onLoadStart?.();
	try {
		await loadScript(src);
		if (!isFunction(window.Hls)) throw Error("");
		const ctor = window.Hls;
		callbacks.onLoaded?.(ctor);
		return ctor;
	} catch (err) {
		callbacks.onLoadError?.(err);
	}
}
//#endregion
//#region src/providers/hls/provider.ts
const JS_DELIVR_CDN = "https://cdn.jsdelivr.net";
/**
* The HLS provider introduces support for HLS streaming via the popular `hls.js`
* library. HLS streaming is either [supported natively](https://caniuse.com/?search=hls) (generally
* on iOS), or in environments that [support the Media Stream API](https://caniuse.com/?search=mediastream).
*
* @docs {@link https://www.vidstack.io/docs/player/providers/hls}
* @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/video}
* @see {@link https://github.com/video-dev/hls.js/blob/master/docs/API.md}
* @example
* ```html
* <media-player
*   src="https://files.vidstack.io/hls/index.m3u8"
*   poster="https://files.vidstack.io/poster.png"
* >
*   <media-provider></media-provider>
* </media-player>
* ```
*/
var HLSProvider = class extends VideoProvider {
	$$PROVIDER_TYPE = "HLS";
	#ctor = null;
	#controller = new HLSController(this.video, this.ctx);
	/**
	* The `hls.js` constructor.
	*/
	get ctor() {
		return this.#ctor;
	}
	/**
	* The current `hls.js` instance.
	*/
	get instance() {
		return this.#controller.instance;
	}
	/**
	* Whether `hls.js` is supported in this environment.
	*/
	static supported = isHLSSupported();
	get type() {
		return "hls";
	}
	get canLiveSync() {
		return true;
	}
	#library = `${JS_DELIVR_CDN}/npm/hls.js@^1.5.0/dist/hls.min.js`;
	/**
	* The `hls.js` configuration object.
	*
	* @see {@link https://github.com/video-dev/hls.js/blob/master/docs/API.md#fine-tuning}
	*/
	get config() {
		return this.#controller.config;
	}
	set config(config) {
		this.#controller.config = config;
	}
	/**
	* The `hls.js` constructor (supports dynamic imports) or a URL of where it can be found.
	*
	* @defaultValue `https://cdn.jsdelivr.net/npm/hls.js@^1.0.0/dist/hls.min.js`
	*/
	get library() {
		return this.#library;
	}
	set library(library) {
		this.#library = library;
	}
	preconnect() {
		if (!isString(this.#library)) return;
		preconnect(this.#library);
	}
	setup() {
		super.setup();
		new HLSLibLoader(this.#library, this.ctx, (ctor) => {
			this.#ctor = ctor;
			this.#controller.setup(ctor);
			this.ctx.notify("provider-setup", this);
			const src = peek(this.ctx.$state.source);
			if (src) this.loadSource(src);
		});
	}
	async loadSource(src, preload) {
		if (!isString(src.src)) {
			this.removeSource();
			return;
		}
		this.media.preload = preload || "";
		this.appendSource(src, "application/x-mpegurl");
		this.#controller.loadSource(src);
		this.currentSrc = src;
	}
	/**
	* The given callback is invoked when a new `hls.js` instance is created and right before it's
	* attached to media.
	*/
	onInstance(callback) {
		const instance = this.#controller.instance;
		if (instance) callback(instance);
		return this.#controller.onInstance(callback);
	}
	destroy() {
		this.#controller.destroy();
	}
};
//#endregion
export { HLSProvider };
